<template>
  <transition name="scroll-fade" mode="out-in">
    <slot v-if="isValidSingleChild" />
    <slot v-else-if="minMaxMap" :id="minMaxMapId" />
    <component
      :is="el || 'div'"
      v-else
      v-show="mode === 'opacity' ? true : showContent"
      v-bind="modeProps"
      class="scroll-fade"
      :class="[`scroll-fade--mode-${mode}`]"
    >
      <slot />
    </component>
  </transition>
</template>

<script>
const isElementNode = el =>
  (el instanceof Element || el instanceof HTMLDocument) && // https://stackoverflow.com/a/36894871
  el.nodeType === Node.ELEMENT_NODE;

const isVisible = (min, max, scrollPosition) => {
  if (scrollPosition === undefined) return true;

  const overMin = min ? min < scrollPosition : true;
  const underMax = max ? scrollPosition < max : true;

  return overMin && underMax;
};

export default {
  name: 'ScrollFade',

  props: {
    el: {
      type: String,
      default: undefined,
    },
    scrollPosition: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      default: undefined,
    },
    max: {
      type: Number,
      default: undefined,
    },
    mode: {
      type: String,
      default: 'display',
      validator: mode => ['display', 'opacity'].includes(mode),
    },
    minMaxMap: {
      type: Object,
      default: undefined,
    },
  },

  computed: {
    hasSingleRootElement() {
      return !!this.$slots.default && this.$slots.default.length === 1;
    },

    isValidSingleChild() {
      if (!this.hasSingleRootElement) return false;
      const el = this.$slots.default[0];
      return isElementNode(el);
    },

    showContent() {
      return isVisible(this.min, this.max, this.scrollPosition);
    },

    modeProps() {
      if (this.mode !== 'opacity') return;

      return {
        style: this.showContent
          ? {
              opacity: '',
              key: 'show',
            }
          : {
              opacity: 0,
              key: 'hide',
            },
      };
    },

    minMaxMapId() {
      if (!this.minMaxMap) return;

      const isOneVisible = minMaxEntries =>
        minMaxEntries.some(({ min, max } = {}) => isVisible(min, max, this.scrollPosition));

      const entry = Object.entries(this.minMaxMap).find(([, minMaxEntries]) =>
        isOneVisible(minMaxEntries)
      );
      if (!entry) return;

      return entry[0]; // id
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-fade--mode-opacity,
.scroll-fade-enter-active,
.scroll-fade-leave-active {
  transition: opacity 0.3s ease;
}

.scroll-fade-enter,
.scroll-fade-leave-to {
  opacity: 0;
}
</style>
